import { Suspense, lazy, useState, type FormEvent } from "react";
import { Box, IconButton } from "@mui/material";
import { useTranslation } from "next-i18next";
import { IconClose } from "src/components/icons-mui/close";
import { ScrollShadowIndicator } from "src/components/modals/scroll-indicator/scroll-shadow-indicator";
import { Button } from "src/components-mui/base/button/button";
import { Typography } from "src/components-mui/base/typography/typography";
import { WfDialog, WF_DIALOG_SPACE_VARIANTS } from "src/components-mui/common/dialog/wf-dialog";
import { blurActiveElement } from "src/utils/dom/focus/focus";
import { createMiddleClickHandler } from "src/utils/dom/mouse/mouse";
import {
    CONTENT_SETTINGS_MODAL_HEADING_ID,
    CONTENT_SETTINGS_MODAL_SHOW_BUTTON_TEST_ID,
    CONTENT_SETTINGS_MODAL_DELETE_SELECTION_BUTTON_TEST_ID,
} from "./adjust-figures-preferences-modal.test-ids";
import type { IAdjustFiguresPreferencesModalProps, IFigure } from "./adjust-figures-preferences-modal.types";
import { areFigureSelectionsEqual } from "./adjust-figures-preferences-modal.utils";
import { DragAndDropSortSectionSkeleton } from "./sections/drag-and-drop-sort-section-skeleton";
import type { IDragAndDropSortSectionProps } from "./sections/drag-and-drop-sort-section.types";
import { FiguresSection } from "./sections/figures-section";
import { useSelectionPicker } from "./selection-picker/use-selection-picker";

const DragAndDropSortSection = lazy(() => import("./sections/drag-and-drop-sort-section").then(mod => ({ default: mod.DragAndDropSortSection })));

const LazyLoadedDragAndDropSortSection = (props: IDragAndDropSortSectionProps) => (
    <Suspense fallback={<DragAndDropSortSectionSkeleton description={props.description} />}>
        <DragAndDropSortSection {...props} />
    </Suspense>
);

export const AdjustFiguresPreferencesModal = ({
    figureSections,
    onApply,
    selectedFigures,
    selfInvested = false,
    investable = false,
    children,
    filterSection: FilterSectionComponent,
    maxSelectable = 4,
    translations,
}: IAdjustFiguresPreferencesModalProps) => {
    const { t } = useTranslation("adjust-preferences-modal");
    const { t: tCommon } = useTranslation("common");

    const [isOpen, setIsOpen] = useState(false);
    const [hasBeenOpened, setHasBeenOpened] = useState(false);
    const [modalInvestable, setModalInvestable] = useState(investable);
    const [modalSelfInvested, setModalSelfInvested] = useState(selfInvested);
    const picker = useSelectionPicker<IFigure>({ defaultSelected: selectedFigures, maxSelectable });

    const openDialog = () => {
        blurActiveElement();
        setIsOpen(true);
    };

    const closeDialog = () => {
        blurActiveElement();
        setIsOpen(false);
    };

    const handleOpen = () => {
        picker.reset(selectedFigures);
        setModalInvestable(investable);
        setModalSelfInvested(selfInvested);
        setHasBeenOpened(true);
        openDialog();
    };

    const handleClose = () => closeDialog();

    const handleApply = () => {
        const hasSelectionChanged = !areFigureSelectionsEqual({ current: picker.selected, previous: selectedFigures });
        const hasFilterChanged = modalSelfInvested !== selfInvested || modalInvestable !== investable;

        if (!hasSelectionChanged && !hasFilterChanged) {
            closeDialog();
            return;
        }

        onApply(picker.selected, modalSelfInvested, modalInvestable);
        closeDialog();
    };

    const handleClear = () => {
        picker.clear();
        setModalInvestable(false);
        setModalSelfInvested(false);
    };

    const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        handleApply();
    };

    const title = translations.title;
    const descriptionOrdering = translations.descriptionOrdering;
    const descriptionSelecting = translations.descriptionSelecting;

    return (
        <>
            {children({ onClick: handleOpen })}
            <WfDialog
                isOpen={isOpen}
                onClose={handleClose}
                showCloseButton={false}
                ariaLabelledBy={CONTENT_SETTINGS_MODAL_HEADING_ID}
                space={WF_DIALOG_SPACE_VARIANTS.NONE}
            >
                <Box component="form" onSubmit={handleSubmit} sx={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>
                    <Box
                        component="header"
                        id={CONTENT_SETTINGS_MODAL_HEADING_ID}
                        sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            borderBottom: "1px solid",
                            borderColor: "gray.100",
                            px: { xs: 3, sm: 5 },
                            py: { xs: 1, sm: 3 },
                        }}
                    >
                        <Typography
                            component="h2"
                            sx={{
                                fontSize: "2xl",
                                fontWeight: "bold",
                            }}
                        >
                            {title}
                        </Typography>
                        <IconButton
                            type="button"
                            variant="ghost"
                            size="small"
                            aria-label={tCommon("close")}
                            onClick={handleClose}
                            sx={{ borderRadius: "6px", ml: 2 }}
                        >
                            <IconClose sx={{ width: "24px", height: "24px" }} />
                        </IconButton>
                    </Box>
                    <ScrollShadowIndicator px={[3, 5]} sx={{ flex: 1, minHeight: 0 }}>
                        {FilterSectionComponent && (
                            <Box
                                sx={{
                                    pt: 2,
                                }}
                            >
                                <FilterSectionComponent
                                    investable={modalInvestable}
                                    selfInvested={modalSelfInvested}
                                    onToggleInvestable={() => setModalInvestable(prev => !prev)}
                                    onToggleSelfInvested={() => setModalSelfInvested(prev => !prev)}
                                />
                            </Box>
                        )}
                        {hasBeenOpened ? <LazyLoadedDragAndDropSortSection picker={picker} description={descriptionOrdering} pt={3} /> : null}
                        <FiguresSection picker={picker} figureSections={figureSections} description={descriptionSelecting} pt={3} />
                    </ScrollShadowIndicator>
                    <Box
                        sx={{
                            px: { xs: 3, sm: 5 },
                            pt: { xs: 3, sm: 4 },
                            pb: { xs: "calc(24px + env(safe-area-inset-bottom, 0px))", sm: 4 },
                            gap: 1,
                            display: "flex",
                            justifyContent: "space-between",
                            backgroundColor: "white",
                            flexShrink: 0,
                        }}
                    >
                        <Button
                            type="button"
                            disabled={picker.isEmpty}
                            size={["small", "medium"]}
                            onClick={handleClear}
                            variant="outlined"
                            color="gray"
                            data-test-id={CONTENT_SETTINGS_MODAL_DELETE_SELECTION_BUTTON_TEST_ID}
                            sx={{ flex: { xs: 1, sm: "initial" } }}
                        >
                            {t("buttons.delete-selection")}
                        </Button>
                        <Button
                            type="submit"
                            disabled={picker.isEmpty}
                            size={["small", "medium"]}
                            onMouseUp={createMiddleClickHandler(handleApply)}
                            data-test-id={CONTENT_SETTINGS_MODAL_SHOW_BUTTON_TEST_ID}
                            sx={{ flex: { xs: 1, sm: "initial" } }}
                        >
                            {t("buttons.show")}
                        </Button>
                    </Box>
                </Box>
            </WfDialog>
        </>
    );
};
